import { Component, signal, computed, inject, effect, viewChild, ElementRef, untracked } from "@angular/core";
import { JumpstartComponentsModule, OpenPageEvent, ResultsPerPageChangeEvent, SimpleTableComponent } from "@wk/components-v3-angular17";
import { WkDataTableHeader } from "@app/shared/components/data-table/header";
import { WkDataTable } from "@components/data-table/classes";
import { WkDataTableSearchService } from "@app/shared/components/data-table/searchbar";
import { WkDataTableStoreService } from "@components/data-table/store";
import type { WkDataTableInternalData } from "@components/data-table/data-table.types";
import { WkDataTableItem } from "@app/shared/components/data-table/item";

@Component({
    selector: "wk-data-table-paging",
    templateUrl: "./data-table-paging.component.html",
    styleUrls: ["./data-table-paging.component.scss", "../data-table.scss", "../data-table-row.scss"],
    imports: [WkDataTableHeader, JumpstartComponentsModule, WkDataTableItem],
    host: {
        class: "wk-data-table__paging",
    },
})
export class WkDataTablePaging<TItem> extends WkDataTable<TItem> {
    private readonly dataTableSearchService = inject<WkDataTableSearchService<TItem>>(WkDataTableSearchService);
    protected readonly dataTableStore = inject(WkDataTableStoreService);

    private table = viewChild.required(SimpleTableComponent, { read: ElementRef });
    private tableHead = viewChild(WkDataTableHeader<TItem>, { read: ElementRef });

    constructor() {
        super();

        // Set results per page based on table body height
        effect((onCleanup) => {
            const tableRef = this.table();
            const tableHeadRef = this.tableHead();
            const rowHeight = this.rowHeight();
            if (!tableRef || !tableHeadRef || !rowHeight) return;

            queueMicrotask(() => {
                const tableEl = (tableRef.nativeElement as HTMLElement).querySelector(".cg3-simple-table");
                if (!tableEl) return;
                const tableHeadEl = (tableHeadRef.nativeElement as HTMLElement).querySelector(".wk-data-table__heading");
                if (!tableHeadEl) return;
                const height = tableEl.getBoundingClientRect().height - tableHeadEl.getBoundingClientRect().height;
                const maxRows = Math.floor(height / rowHeight);
                untracked(() => this.resultsPerPage.set(maxRows));

                let debounceTimer: ReturnType<typeof setTimeout> | undefined;
                const ro = new ResizeObserver(() => {
                    clearTimeout(debounceTimer);

                    debounceTimer = setTimeout(() => {
                        const height = tableEl.getBoundingClientRect().height - tableHeadEl.getBoundingClientRect().height;
                        const maxRows = Math.floor(height / rowHeight);
                        untracked(() => this.resultsPerPage.set(maxRows));
                    }, 100);
                });

                ro.observe(tableEl);

                onCleanup(() => {
                    clearTimeout(debounceTimer);
                    ro.disconnect();
                });
            });
        });

        effect(() => {
            const _ = this.dataTableSearchService.searchValue();
            this.currentPage.set(1);
        });
    }

    protected readonly itemsInPage = computed<WkDataTableInternalData<TItem>>(() => {
        const items = this.items();
        if (!items?.length) return [];

        return items.slice((this.currentPage() - 1) * this.resultsPerPage(), this.currentPage() * this.resultsPerPage());
    });

    protected resultsPerPage = signal<number>(10);
    protected currentPage = signal<number>(1);
    protected getPagesCount = computed<number>(() => {
        const items = this.items();
        const resultsPerPage = this.resultsPerPage();
        return Math.ceil(items.length / resultsPerPage);
    });
    protected pageStart = computed<number>(() => {
        return (this.currentPage() - 1) * this.resultsPerPage();
    });

    protected openPage(event: OpenPageEvent) {
        this.currentPage.set(event.page);
    }

    protected onResultsPerPageChange(event: ResultsPerPageChangeEvent) {
        this.resultsPerPage.set(event.resultsPerPage);
    }
}
